/**
 * Type alias to infer the inner type of a Promise.
 * @template T - The type of the Promise's resolved value or rejection reason.
 */
export type UnwrapPromise<T> = T extends Promise<infer U> ? U : T;

export type Outcome<T extends Exclude<unknown, undefined>, TError = unknown> =
  | [Exclude<T, undefined>, undefined]
  | [undefined, Exclude<TError, T>];
/**
 * Creates a new asynchronous function that returns either a resolved value or an error.
 * @template T - The type of the returned value when the function is called successfully.
 * @template TError - The type of the error to be thrown when the function call fails.
 * @param {Function} error - A function that creates and returns the error instance.
 * @param {Function} fn - The original asynchronous function.
 * @returns {Function} A new asynchronous function that either resolves with the returned value or rejects with an error.
 */
export function returnValueWithErrorAsync<
  T extends (...args: any) => any,
  TError = unknown
>(
  error: (x: unknown) => TError,
  fn: T
): (
  ...args: Parameters<T>
) => Promise<Outcome<UnwrapPromise<ReturnType<T>>, TError>> {
  return async (...args: any[]) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- just calling original fn
      const x = await fn(...args);
      return [x, undefined] as any;
    } catch (err) {
      console.error("Unhandled exception was thrown", err);
      return [undefined, error(err)] as any;
    }
  };
}

/**
 * Converts a value to its specified type.
 * @template T - The target type of the given value.
 * @param {any} val - Any JavaScript value.
 * @returns {T} The given value, casted to the target type.
 */
export function ErrorType<T>(val: any): T {
  return val as T;
}

/**
 * A higher order function that returns a promise resolving either with the value or throwing an error.
 * @template TValue - The type of the value to be returned when no error is encountered.
 * @template TError - The type of the error to be thrown when an error occurs.
 * @param {function(): Promise<[TValue, TError]>} fn - A function returning a promise that resolves with an array containing the value and error.
 * @returns {Promise<TValue>} A promise that resolves with the value or rejects with an error.
 */
export function returnValueOrThrow<TValue, TError>(
  fn: () => Promise<[TValue, TError]>
) {
  return async () => {
    const [val, err] = await fn();
    if (!err) {
      return val as Exclude<TValue, undefined>;
    }
    throw err;
  };
}
